import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { LanguageSwitcher } from '../ui/language-switcher';
import { LoadingSpinner } from '../ui/loading';
import { api } from '../../lib/api';
import { ReactComponent as LurganLogo } from '../../assets/logos-svg/Lurgan-Horizontal-Vector_Lurgan-Logo-Dubh.svg';
import { ReactComponent as BainisteoirLogo } from '../../assets/logos-svg/Bainisteoir-Lurgan-Vector.svg';

export function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await api.auth.forgotPassword(email);
    } catch (_) {
      // Silently swallow — always show success to avoid revealing if email exists
    } finally {
      setIsSubmitting(false);
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }}>
        <LurganLogo className="h-14 mb-6 [&_path]:fill-black dark:[&_path]:fill-white" aria-label="Coláiste Lurgan" />
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-end mb-2">
              <LanguageSwitcher />
            </div>
            <BainisteoirLogo className="h-10 mx-auto mb-3 text-black dark:text-white" aria-label="Bainisteoir" />
            <CardTitle className="text-lg font-semibold text-muted-foreground">
              {t('forgotPassword.successTitle')}
            </CardTitle>
            <CardDescription>
              {t('forgotPassword.successDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link
              to="/login"
              className="block w-full text-center text-sm text-muted-foreground hover:text-foreground underline mt-2"
            >
              {t('forgotPassword.backToLogin')}
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }}>
      <LurganLogo className="h-14 mb-6 [&_path]:fill-black dark:[&_path]:fill-white" aria-label="Coláiste Lurgan" />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-end mb-2">
            <LanguageSwitcher />
          </div>
          <BainisteoirLogo className="h-10 mx-auto mb-3 text-black dark:text-white" aria-label="Bainisteoir" />
          <CardTitle className="text-lg font-semibold text-muted-foreground">
            {t('forgotPassword.title')}
          </CardTitle>
          <CardDescription>
            {t('forgotPassword.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('forgotPassword.emailLabel')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isSubmitting}
                autoComplete="email"
              />
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size="sm" className="border-primary-foreground" />
                  <span>{t('forgotPassword.submit')}</span>
                </div>
              ) : (
                t('forgotPassword.submit')
              )}
            </Button>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-muted-foreground hover:text-foreground underline"
              >
                {t('forgotPassword.backToLogin')}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default ForgotPassword;
